import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { generateUUID } from "../utils/idempotency";
import {
  createOrder,
  reset as resetOrder,
} from "../features/orders/orderSlice";
import { addAddress, addCard } from "../features/auth/authSlice";
import { getCart } from "../features/cart/cartSlice";
import { getShippingFee } from "../features/shipping/shippingSlice";
import { spendCredits, rewardCredits } from "../features/credits/creditsSlice";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  HiOutlineLocationMarker,
  HiOutlineCreditCard,
  HiOutlineShieldCheck,
  HiCheckCircle,
  HiOutlineLightningBolt,
} from "react-icons/hi";
import { FaCoins, FaCcVisa, FaCcMastercard } from "react-icons/fa";
import {
  dollarToCredits,
  calculateCreditDiscount,
  calculateEarnedCredits,
  formatCredits,
} from "../utils/creditConfig";

function Checkout() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const cartItems = user?.data?.cart || [];
  const creditBalance = user?.data?.balance || 0;
  const { isLoading, isSuccess } = useSelector((state) => state.orders);
  const { shippingFee: backendShippingFee, isLoading: isShippingLoading } =
    useSelector((state) => state.shippingFee);

  // Form Data for NEW entries
  const [formData, setFormData] = useState({
    address: "",
    city: "",
    state: "",
    zipCode: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
    cardName: "",
  });

  // Logic States
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1); // -1 means "New Address"
  const [selectedCardIndex, setSelectedCardIndex] = useState(-1); // -1 means "New Card"
  const [saveAddress, setSaveAddress] = useState(false);
  const [saveCard] = useState(false);
  const [payWithCredits, setPayWithCredits] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);

  // Initialize selection based on existing user data
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Default to first saved address if available
    if (user?.data?.addresses?.length > 0) {
      setSelectedAddressIndex(0);
    } else {
      setSelectedAddressIndex(-1);
    }

    // Default to first saved card if available
    if (user?.data?.paymentMethods?.length > 0) {
      setSelectedCardIndex(0);
    } else {
      setSelectedCardIndex(-1);
    }
  }, [user, navigate]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(resetOrder());
      navigate("/order-success");
      dispatch(getCart({ email: user?.data?.email }));
    }
  }, [isSuccess, navigate, dispatch, user]);

  const getShippingAddress = useCallback(() => {
    if (
      selectedAddressIndex !== -1 &&
      user?.data?.addresses?.[selectedAddressIndex]
    ) {
      const saved = user.data.addresses[selectedAddressIndex];
      return {
        address: saved.address,
        city: saved.city,
        state: saved.state,
        zipCode: saved.zipCode || "", // Handle missing zip if legacy data
      };
    }
    return {
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
    };
  }, [
    selectedAddressIndex,
    user?.data?.addresses,
    formData.address,
    formData.city,
    formData.state,
    formData.zipCode,
  ]);

  // Dynamic Shipping Fee Fetching
  useEffect(() => {
    // Get the destination state from either saved address or form
    let destinationState = "";
    if (
      selectedAddressIndex !== -1 &&
      user?.data?.addresses?.[selectedAddressIndex]
    ) {
      destinationState = user.data.addresses[selectedAddressIndex].state;
    } else {
      destinationState = formData.state;
    }

    if (destinationState && cartItems?.length > 0) {
      dispatch(
        getShippingFee({
          originState: "lagos",
          destinationState: destinationState,
          itemsCount: cartItems.length,
        })
      );
    }
  }, [
    selectedAddressIndex,
    formData.state,
    cartItems?.length,
    dispatch,
    user?.data?.addresses,
  ]);

  useEffect(() => {
    if (backendShippingFee?.data?.shippingFee !== undefined) {
      setShippingCost(backendShippingFee.data.shippingFee);
    }
  }, [backendShippingFee]);

  const onChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const getPaymentMethod = () => {
    if (payWithCredits) {
      return "Store Credits";
    }
    if (
      selectedCardIndex !== -1 &&
      user?.data?.paymentMethods?.[selectedCardIndex]
    ) {
      const card = user.data.paymentMethods[selectedCardIndex];
      return `Saved Card: ${card.name} ending in ${
        card.last4 || card.number.slice(-4)
      }`;
    }
    return "Card (Simulated)";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (cartItems?.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    // Calculate totals using dynamic credit config
    const subtotal =
      cartItems?.reduce((t, i) => t + parseFloat(i.price), 0) || 0;
    const totalWithShipping = subtotal + shippingCost;
    const creditDiscount = payWithCredits
      ? calculateCreditDiscount(subtotal, true)
      : 0;
    const finalTotal = totalWithShipping - creditDiscount;
    const creditsNeeded = dollarToCredits(finalTotal);

    if (payWithCredits && creditBalance < creditsNeeded) {
      toast.error(
        `Insufficient credits. You need ${formatCredits(
          creditsNeeded
        )} credits but have ${formatCredits(creditBalance)}.`
      );
      return;
    }

    const shippingAddress = getShippingAddress();
    const orderData = {
      email: user.data.email,
      items: cartItems,
      total: finalTotal,
      shippingAddress,
      paymentMethod: getPaymentMethod(),
    };

    const idempotencyKey = generateUUID();

    // Handle "Save for later" Logic
    if (selectedAddressIndex === -1 && saveAddress) {
      dispatch(
        addAddress({
          email: user.data.email,
          address: shippingAddress,
        })
      );
    }

    if (selectedCardIndex === -1 && saveCard) {
      dispatch(
        addCard({
          email: user.data.email,
          card: {
            name: formData.cardName || "My Card",
            number: formData.cardNumber,
            expiry: formData.expiry,
            cvv: formData.cvv,
            type: "visa", // simplified
          },
        })
      );
    }

    // Spend credits if paying with credits
    if (payWithCredits) {
      try {
        await dispatch(
          spendCredits({
            email: user.data.email,
            amount: creditsNeeded,
            description: `Order payment - ${cartItems.length} items`,
          })
        ).unwrap();
      } catch (error) {
        toast.error("Failed to process credits payment");
        return;
      }
    } else {
      // Award loyalty credits if paying with money
      const creditsToEarn = calculateEarnedCredits(subtotal);
      try {
        dispatch(
          rewardCredits({
            email: user.data.email,
            amount: creditsToEarn,
            description: `Loyalty reward - Order with ${cartItems.length} items`,
          })
        );
      } catch (error) {
        console.error("Failed to award loyalty credits", error);
      }
    }

    dispatch(createOrder({ orderData, idempotencyKey }));
  };

  const cartSubtotal =
    cartItems?.reduce((total, item) => total + parseFloat(item.price), 0) || 0;

  // Dynamic calculations including shipping cost
  const creditDiscount = payWithCredits
    ? calculateCreditDiscount(cartSubtotal, true)
    : 0;
  const finalTotal = cartSubtotal + shippingCost - creditDiscount;
  const creditsNeeded = dollarToCredits(finalTotal);

  return (
    <div className="min-h-screen bg-[#0b0e14] py-16 px-4 md:px-8 selection:bg-cyan-500/30 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[140px] pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 -right-40 w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[140px] pointer-events-none animate-pulse" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-6">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping" />
              Secure Protocol Alpha
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Deployment <span className="text-cyan-400">Checkout</span>
            </h1>
          </div>
          <div className="flex items-center gap-3 bg-white/5 backdrop-blur-xl px-6 py-3 rounded-2xl border border-white/10">
            <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center text-green-400">
              <HiOutlineShieldCheck className="text-2xl" />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest leading-none mb-1">
                Status
              </p>
              <p className="text-sm font-bold text-white leading-none">
                SECURE_TUNNEL_ACTIVE
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10"
        >
          <div className="lg:col-span-8 space-y-8">
            {/* Step 1: Shipping */}
            <div className="bg-white/5 backdrop-blur-2xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl relative group overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500/0 via-cyan-500/50 to-cyan-500/0" />

              <div className="flex items-center gap-6 mb-10">
                <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded-2xl flex items-center justify-center font-black italic shadow-lg shadow-cyan-500/10 text-xl">
                  01
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter flex items-center gap-3">
                    <HiOutlineLocationMarker className="text-cyan-400" /> Drop
                    Point Matrix
                  </h2>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em]">
                    Shipping Configuration
                  </p>
                </div>
              </div>

              {/* Saved Addresses List */}
              {user?.data?.addresses?.length > 0 && (
                <div className="grid grid-cols-1 gap-4 mb-8">
                  {user.data.addresses.map((addr, idx) => (
                    <div
                      key={addr.id || idx}
                      onClick={() => setSelectedAddressIndex(idx)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedAddressIndex(idx);
                        }
                      }}
                      className={`p-5 rounded-2xl border-2 cursor-pointer flex items-center justify-between transition-all duration-300 ${
                        selectedAddressIndex === idx
                          ? "border-cyan-500/50 bg-cyan-500/5 shadow-inner"
                          : "border-white/5 bg-white/5 hover:bg-white/[0.08]"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            selectedAddressIndex === idx
                              ? "bg-cyan-400 animate-pulse shadow-[0_0_10px_rgba(34,211,238,0.8)]"
                              : "bg-white/20"
                          }`}
                        />
                        <div>
                          <p className="font-bold text-sm text-white">
                            {addr.address}
                          </p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest italic leading-none mt-1">
                            {addr.city}, {addr.state} {addr.zipCode}
                          </p>
                        </div>
                      </div>
                      {selectedAddressIndex === idx && (
                        <HiCheckCircle className="text-2xl text-cyan-400" />
                      )}
                    </div>
                  ))}

                  <div
                    onClick={() => setSelectedAddressIndex(-1)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSelectedAddressIndex(-1);
                      }
                    }}
                    className={`p-5 rounded-2xl border-2 border-dashed cursor-pointer flex items-center justify-between transition-all duration-300 ${
                      selectedAddressIndex === -1
                        ? "border-cyan-500 bg-cyan-500/5"
                        : "border-white/10 hover:border-cyan-500/30"
                    }`}
                  >
                    <p className="font-bold text-sm text-gray-400 italic">
                      + Link New Matrix Address
                    </p>
                  </div>
                </div>
              )}

              {/* New Address Form */}
              {selectedAddressIndex === -1 && (
                <div className="space-y-8 animate-fadeIn">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="md:col-span-2">
                      <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] mb-2 block ml-1">
                        Street Location
                      </label>
                      <input
                        required={selectedAddressIndex === -1}
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={onChange}
                        placeholder="INPUT_ADDRESS_DATA"
                        className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-bold text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] mb-2 block ml-1">
                        Sector (City)
                      </label>
                      <input
                        required={selectedAddressIndex === -1}
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={onChange}
                        placeholder="CITY_ID"
                        className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-bold text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] mb-2 block ml-1">
                          Region
                        </label>
                        <input
                          required={selectedAddressIndex === -1}
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={onChange}
                          placeholder="STATE"
                          className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-bold text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] mb-2 block ml-1">
                          Zip
                        </label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={onChange}
                          placeholder="ZIP_CODE"
                          className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all font-bold text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-4">
                    <input
                      type="checkbox"
                      id="saveAddress"
                      checked={saveAddress}
                      onChange={(e) => setSaveAddress(e.target.checked)}
                      className="w-6 h-6 accent-cyan-500 rounded-lg cursor-pointer bg-white/5 border-white/10 shadow-inner"
                    />
                    <label
                      htmlFor="saveAddress"
                      className="text-xs font-black text-gray-400 uppercase tracking-widest cursor-pointer"
                    >
                      Archive for future deployments
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Payment */}
            <div className="bg-white/5 backdrop-blur-2xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl relative group overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-purple-500/0" />

              <div className="flex items-center gap-6 mb-10">
                <div className="w-14 h-14 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-2xl flex items-center justify-center font-black italic shadow-lg shadow-purple-500/10 text-xl">
                  02
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter flex items-center gap-3">
                    <HiOutlineCreditCard className="text-purple-400" /> Loot
                    Extraction Key
                  </h2>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em]">
                    Payment Configuration
                  </p>
                </div>
              </div>

              {/* Pay with Credits Option */}
              <div
                onClick={() => setPayWithCredits(!payWithCredits)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setPayWithCredits(!payWithCredits);
                  }
                }}
                className={`p-6 rounded-[2rem] border-2 cursor-pointer transition-all duration-300 mb-8 relative group/card ${
                  payWithCredits
                    ? "border-amber-500/50 bg-amber-500/5"
                    : "border-white/5 bg-white/5 hover:border-amber-500/30"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                        payWithCredits
                          ? "bg-amber-500 text-white shadow-lg shadow-amber-500/20"
                          : "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      }`}
                    >
                      <FaCoins className="text-2xl" />
                    </div>
                    <div>
                      <p className="font-black text-white uppercase italic tracking-tighter text-lg flex items-center gap-2">
                        Gamer Pts Override
                        <span className="text-[10px] bg-amber-500 text-[#0b0e14] px-2 py-0.5 rounded-full font-black animate-pulse">
                          {formatCredits(creditBalance)} STOCK
                        </span>
                      </p>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">
                        Deduct from achievement balance (
                        {formatCredits(creditsNeeded)} required)
                      </p>
                    </div>
                  </div>
                  {payWithCredits && (
                    <HiCheckCircle className="text-3xl text-amber-500 shadow-amber-500/20 shadow-xl" />
                  )}
                </div>
              </div>

              {!payWithCredits && (
                <div className="space-y-8 animate-fadeIn">
                  {/* Saved Cards */}
                  {user?.data?.paymentMethods?.length > 0 && (
                    <div className="grid grid-cols-1 gap-4">
                      {user.data.paymentMethods.map((card, idx) => (
                        <div
                          key={card.id || idx}
                          onClick={() => setSelectedCardIndex(idx)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              setSelectedCardIndex(idx);
                            }
                          }}
                          className={`p-5 rounded-2xl border-2 cursor-pointer flex items-center justify-between transition-all duration-300 ${
                            selectedCardIndex === idx
                              ? "border-purple-500/50 bg-purple-500/5"
                              : "border-white/5 bg-white/5 hover:bg-white/[0.08]"
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="text-white text-2xl">
                              {card.type === "visa" ? (
                                <FaCcVisa />
                              ) : (
                                <FaCcMastercard />
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-sm text-white">
                                {card.name}
                              </p>
                              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest italic leading-none mt-1">
                                SENSITIVE_KEY: ••••{" "}
                                {card.last4 || card.number.slice(-4)}
                              </p>
                            </div>
                          </div>
                          {selectedCardIndex === idx && (
                            <HiCheckCircle className="text-2xl text-purple-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCardIndex === -1 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1">
                          Cardholder Entity
                        </label>
                        <input
                          required={selectedCardIndex === -1}
                          type="text"
                          name="cardName"
                          value={formData.cardName}
                          onChange={onChange}
                          placeholder="ENTITY_NAME"
                          className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all font-bold text-sm"
                        />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1">
                          Credit ID Number
                        </label>
                        <input
                          required={selectedCardIndex === -1}
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={onChange}
                          placeholder="XXXX XXXX XXXX XXXX"
                          className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all font-bold text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1">
                          Expiry Matrix
                        </label>
                        <input
                          required={selectedCardIndex === -1}
                          type="text"
                          name="expiry"
                          value={formData.expiry}
                          onChange={onChange}
                          placeholder="MM/YY"
                          className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all font-bold text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase text-gray-500 tracking-[0.3em] block ml-1">
                          C_V_V
                        </label>
                        <input
                          required={selectedCardIndex === -1}
                          type="password"
                          name="cvv"
                          value={formData.cvv}
                          onChange={onChange}
                          placeholder="***"
                          className="w-full px-6 py-5 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all font-bold text-sm text-center tracking-widest"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white/5 backdrop-blur-3xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl sticky top-28 overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full -translate-y-16 translate-x-16 blur-2xl pointer-events-none" />

              <h2 className="text-xl font-black text-white mb-8 border-b border-white/5 pb-4 uppercase tracking-[0.2em] italic">
                Deployment Digest
              </h2>

              <div className="space-y-4 mb-10 max-h-72 overflow-y-auto pr-2 custom-scrollbar">
                {cartItems?.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex gap-4 p-3 bg-white/5 border border-white/5 rounded-2xl hover:border-cyan-500/30 transition-all group/item"
                  >
                    <div className="w-16 h-16 bg-white/10 rounded-xl overflow-hidden p-2">
                      <img
                        src={item.image}
                        className="w-full h-full object-contain grayscale group-hover/item:grayscale-0 transition-all duration-500"
                        alt=""
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <p className="font-bold text-xs text-white truncate w-32 mb-1">
                        {item.name}
                      </p>
                      <p className="text-cyan-400 font-black text-[10px] tracking-widest uppercase italic">
                        ${item.price}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4 border-t border-white/5 pt-8">
                <div className="flex justify-between items-center opacity-60">
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Cargo Value
                  </span>
                  <span className="text-xs font-bold text-white">
                    ${cartSubtotal.toFixed(2)}
                  </span>
                </div>
                {payWithCredits && (
                  <div className="flex justify-between items-center text-amber-500 font-bold">
                    <span className="text-[10px] uppercase tracking-widest italic">
                      Achievement Rebate
                    </span>
                    <span className="text-xs">
                      -${creditDiscount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    Logistics Dispatch
                  </span>
                  <span className="text-xs font-bold text-green-500 uppercase tracking-widest">
                    {isShippingLoading
                      ? "SCANNING..."
                      : shippingCost === 0
                      ? "ZERO_FEE"
                      : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>

                <div className="pt-8 border-t border-white/10 mt-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black text-gray-600 uppercase tracking-[0.2em]">
                        Final Authority
                      </p>
                      <h4 className="text-3xl font-black text-white italic tracking-tighter uppercase">
                        Total
                      </h4>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black text-cyan-400 tracking-tighter leading-none mb-1">
                        {payWithCredits
                          ? formatCredits(creditsNeeded)
                          : `$${finalTotal.toFixed(2)}`}
                      </p>
                      <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest">
                        {payWithCredits ? "PTS_RESERVE" : "USD_MATRIX"}
                      </p>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full group relative p-6 bg-white text-[#0b0e14] rounded-[2rem] font-black uppercase tracking-[0.2em] italic text-sm hover:invert transition-all duration-500 shadow-2xl shadow-cyan-500/20 active:scale-[0.98] disabled:opacity-30 flex items-center justify-center gap-3"
                  >
                    {isLoading ? (
                      <Loader size="sm" />
                    ) : (
                      <>
                        Execute Deployment{" "}
                        <HiOutlineLightningBolt className="text-xl" />
                      </>
                    )}
                  </button>

                  <div className="bg-cyan-500/5 border border-cyan-500/10 p-4 rounded-2xl">
                    <p className="text-[9px] text-cyan-500/60 font-medium uppercase tracking-[0.1em] text-center leading-relaxed">
                      By initiating the deployment sequence you authorize the
                      GamerHub protocol to execute the acquisition from your
                      linked matrices.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Checkout;
